import { useTranslation } from 'react-i18next';

import type { WitnessStatus } from '@/api/types';

import type { AttestationFilters } from '@/hooks/useAttestations';

export function FilterBar({
  filters,
  onChange,
}: {
  filters: AttestationFilters;
  onChange: (next: AttestationFilters) => void;
}) {
  const { t } = useTranslation('attestations');

  const update = <K extends keyof AttestationFilters>(
    k: K,
    v: AttestationFilters[K] | undefined,
  ) => {
    const next: AttestationFilters = { ...filters };
    if (v === undefined || v === '') delete next[k];
    else next[k] = v;
    onChange(next);
  };

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_1fr_180px_auto]">
      <label className="flex flex-col gap-1 text-xs">
        <span className="font-medium text-ink-muted">{t('filters.from')}</span>
        <input
          type="datetime-local"
          value={(filters.from_iso ?? '').slice(0, 16)}
          onChange={(e) =>
            update('from_iso', e.target.value ? `${e.target.value}:00Z` : undefined)
          }
          className="rounded-md border border-border bg-surface px-3 py-1.5 text-sm text-ink focus-visible:border-spark"
        />
      </label>
      <label className="flex flex-col gap-1 text-xs">
        <span className="font-medium text-ink-muted">{t('filters.to')}</span>
        <input
          type="datetime-local"
          value={(filters.to_iso ?? '').slice(0, 16)}
          onChange={(e) =>
            update('to_iso', e.target.value ? `${e.target.value}:00Z` : undefined)
          }
          className="rounded-md border border-border bg-surface px-3 py-1.5 text-sm text-ink focus-visible:border-spark"
        />
      </label>
      <label className="flex flex-col gap-1 text-xs">
        <span className="font-medium text-ink-muted">{t('filters.witness')}</span>
        <select
          value={filters.witness_status ?? ''}
          onChange={(e) =>
            update('witness_status', (e.target.value as WitnessStatus) || undefined)
          }
          className="rounded-md border border-border bg-surface px-3 py-1.5 text-sm text-ink focus-visible:border-spark"
        >
          <option value="">{t('filters.all')}</option>
          <option value="ok">{t('filters.ok')}</option>
          <option value="pending">{t('filters.pending')}</option>
          <option value="down">{t('filters.down')}</option>
        </select>
      </label>
      <button
        type="button"
        onClick={() => onChange({})}
        className="self-end rounded-md border border-border bg-surface px-3 py-1.5 text-xs font-medium text-ink-muted hover:border-border-strong hover:text-ink"
      >
        {t('filters.reset')}
      </button>
    </div>
  );
}
